import type { AuthCall } from './types'

export * from './getValidPhoneNumberFromUrl'
export * from './removeKeyFromUrl'
export * from './removeTokenFromUrl'
export * from './decodeToken'
export * from './fetch'
export * from './types'

export type BaseResponseError = {
  response: Response
  text: string | undefined
  json: Record<string, unknown> | null
  status: number
}

export type AuthError = BaseResponseError

export type ValidationError = BaseResponseError & {
  json: Record<string, string[]>
}

export async function authCall({ path, headers = {}, method, body }: AuthCall) {
  const token = AuthClient.getToken()
  const response = await fetch(path, {
    method,
    body,
    headers: {
      ...headers,
      Authorization: 'Bearer ' + token,
    },
  })

  if (!response.ok) {
    const clone = response.clone()
    const status = response.status

    const [json, text] = await Promise.all([
      response.json().catch(() => null),
      clone.text().catch(() => ''),
    ])

    if (status === 422) {
      const error: ValidationError = {
        response,
        text,
        json,
        status,
      }
      throw error
    }

    const error: AuthError = {
      response,
      text,
      json,
      status,
    }

    throw error
  }

  try {
    const res = await response.json()
    return res
  } catch {
    return null
  }
}

class UserAuthClient {
  private token: null | string = null

  public getToken() {
    return this.token
  }

  public setToken(newToken: string) {
    this.token = newToken
  }

  public removeToken() {
    this.token = null
  }

  public logOut(args?: { isTriggeredProgrammatically?: boolean }) {
    this.removeToken()
    /**
     * React router doesn't expose a navigate method which can be used outside of a component
     * the following line causes a page refresh
     * todo: replace with router navigate func when available
     */

    const isNextUrlSupported =
      (window.location.pathname !== '/' || window.location.search) &&
      // If the logout happened due to invalid key&code, do not set next
      !window.location.search.includes('key') &&
      // If the logout was trigged manually, do not set next
      args?.isTriggeredProgrammatically

    const errorMsg = new URLSearchParams(window.location.search).get('errorMsg')

    const params = new URLSearchParams()
    if (!errorMsg && isNextUrlSupported)
      params.set('next', window.location.pathname + window.location.search)
    if (errorMsg) params.set('azureErrorMsg', errorMsg)
    const search = String(params) ? `?${params}` : ''
    window.location.replace(`/logout${search}`)
  }
}

export const AuthClient = new UserAuthClient()
